import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Button,
  Input,
  Modal,
  Form,
  Typography,
  Tag,
  List,
  Avatar,
  message,
  Divider,
  Tabs,
  Tooltip,
  Badge,
} from "antd";
import {
  UserAddOutlined,
  CopyOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  SendOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  getUserReferralInfoAction,
  inviteUserAction,
  removeInvitationAction,
} from "@redux/actions/gamification/referralActions";
import moment from "moment";

const { Text } = Typography;

const ReferralCard = () => {
  const dispatch = useDispatch();
  const { referralInfo } = useSelector((state) => state.referral);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [invitationsModalVisible, setInvitationsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [copied, setCopied] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");

  useEffect(() => {
    dispatch(getUserReferralInfoAction());
  }, [dispatch]);

  const handleCopyReferralLink = () => {
    const referralLink = `${window.location.origin}/signup?ref=${referralInfo?.referralCode}`;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    message.success("Referral link copied to clipboard!");

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const handleInviteUser = async (values) => {
    setInviting(true);
    setInviteError("");
    try {
      const result = await dispatch(inviteUserAction(values.email));

      // Check if the email delivery failed but the invitation was recorded
      if (result.emailError) {
        message.warning(result.message);
        // Keep the modal open with a warning message
        setInviteError(
          "Email couldn't be delivered, but the invitation was recorded. You can try again later."
        );
        setInviting(false);
      } else {
        // Normal success case
        form.resetFields();
        setInviteModalVisible(false);
        dispatch(getUserReferralInfoAction());
        setInviting(false);
      }
    } catch (error) {
      // Display the error message to the user
      let errorMsg = "Failed to send invitation. Please try again.";
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;

        // Special case for existing users
        if (errorMsg.includes("already has an account")) {
          errorMsg =
            "This person already has an account. Share your referral link with someone new!";
        }
      }

      setInviteError(errorMsg);
      message.error(errorMsg);
      console.error("Error inviting user:", error);
      setInviting(false);
    }
  };

  const resetInviteForm = () => {
    form.resetFields();
    setInviteError("");
    setInviteModalVisible(false);
  };

  // Handle invitation retry
  const handleRetryInvitation = async (email) => {
    try {
      await dispatch(inviteUserAction(email));
      dispatch(getUserReferralInfoAction());
      message.success("Invitation sent successfully!");
    } catch (error) {
      let errorMsg = "Failed to send invitation. Please try again.";
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      message.error(errorMsg);
    }
  };

  // Handle removing an invitation
  const handleRemoveInvitation = async (email) => {
    try {
      await dispatch(removeInvitationAction(email));
      dispatch(getUserReferralInfoAction());
      message.success("Invitation removed successfully!");
    } catch (error) {
      message.error("Failed to remove invitation. Please try again.");
    }
  };

  return (
    <Card
      style={{
        background: "white",
        border: "1px solid #e8e8e8",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      bodyStyle={{
        padding: "0",
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                background: "linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "12px",
              }}
            >
              <span style={{ fontSize: "18px" }}>🎁</span>
            </div>
            <Text
              style={{
                color: "#262626",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Invite & Earn
            </Text>
          </div>
          <div
            style={{
              background: "linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)",
              borderRadius: "12px",
              padding: "4px 10px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span style={{ fontSize: "12px" }}>💰</span>
            <Text style={{ color: "#16a34a", fontSize: "12px", fontWeight: "600" }}>
              1000 credits
            </Text>
          </div>
        </div>

        {/* Info banner */}
        <div
          style={{
            background: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)",
            borderRadius: "12px",
            padding: "14px",
            marginBottom: "16px",
          }}
        >
          <Text
            style={{
              fontSize: "13px",
              color: "#595959",
              lineHeight: "1.5",
              fontWeight: "500",
            }}
          >
            ✨ <strong>Invite friends</strong> and earn{" "}
            <strong style={{ color: "#8B5CF6" }}>1000 credits</strong> when they
            sign up!
          </Text>
        </div>

        {/* Referral Stats */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "16px",
          }}
        >
          {[
            { label: "📤 Invited", value: referralInfo?.totalInvites || 0 },
            { label: "✅ Successful", value: referralInfo?.successfulReferrals || 0 },
            { label: "💎 Credits", value: referralInfo?.creditsEarned || 0 },
          ].map((stat, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                background: "linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)",
                border: "1px solid #e8e8e8",
                borderRadius: "10px",
                padding: "12px 8px",
                textAlign: "center",
              }}
            >
              <Text
                style={{
                  color: "#262626",
                  fontSize: "20px",
                  fontWeight: "700",
                  display: "block",
                }}
              >
                {stat.value}
              </Text>
              <Text
                style={{
                  color: "#8c8c8c",
                  fontSize: "11px",
                  fontWeight: "500",
                }}
              >
                {stat.label}
              </Text>
            </div>
          ))}
        </div>

        {/* Milestone Progress */}
        <div
          style={{
            background: "linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)",
            border: "1px solid #e8e8e8",
            borderRadius: "12px",
            padding: "14px",
            marginBottom: "16px",
          }}
        >
          <Text
            style={{
              color: "#262626",
              fontSize: "13px",
              fontWeight: "600",
              display: "block",
              marginBottom: "12px",
            }}
          >
            🏆 Milestone Rewards
          </Text>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {[
              { count: 5, bonus: 500, emoji: "🥉" },
              { count: 15, bonus: 2000, emoji: "🥈" },
              { count: 30, bonus: 5000, emoji: "🥇" },
            ].map((milestone, index) => {
              const isCompleted =
                (referralInfo?.successfulReferrals || 0) >= milestone.count;
              return (
                <Tooltip
                  key={index}
                  title={`Refer ${milestone.count} friends to earn ${milestone.bonus} bonus credits!`}
                >
                  <div
                    style={{
                      textAlign: "center",
                      position: "relative",
                      background: isCompleted
                        ? "linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)"
                        : "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)",
                      border: isCompleted
                        ? "1px solid #86efac"
                        : "1px solid #e8e8e8",
                      borderRadius: "10px",
                      padding: "12px 8px",
                      flex: 1,
                    }}
                  >
                    <Text style={{ fontSize: "16px", display: "block", marginBottom: "4px" }}>
                      {milestone.emoji}
                    </Text>
                    <Text
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: isCompleted ? "#16a34a" : "#262626",
                        display: "block",
                      }}
                    >
                      {milestone.count}
                    </Text>
                    <Text
                      style={{
                        fontSize: "11px",
                        color: "#8B5CF6",
                        fontWeight: "600",
                      }}
                    >
                      +{milestone.bonus}
                    </Text>
                    {isCompleted && (
                      <CheckCircleOutlined
                        style={{
                          position: "absolute",
                          top: "-4px",
                          right: "-4px",
                          color: "#16a34a",
                          fontSize: "14px",
                          background: "white",
                          borderRadius: "50%",
                        }}
                      />
                    )}
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "auto" }}>
          <Button
            type="primary"
            onClick={() => setInviteModalVisible(true)}
            loading={inviting}
            style={{
              background: "linear-gradient(135deg, #8B5CF6, #EC4899)",
              border: "none",
              borderRadius: "10px",
              height: "42px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
            }}
            icon={<UserAddOutlined />}
          >
            Invite Friends
          </Button>

          <Button
            onClick={handleCopyReferralLink}
            style={{
              background: "linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)",
              border: "1px solid #e8e8e8",
              borderRadius: "10px",
              height: "38px",
              fontSize: "13px",
              color: "#8B5CF6",
              fontWeight: "500",
            }}
            icon={copied ? <CheckCircleOutlined /> : <CopyOutlined />}
          >
            {copied ? "✓ Copied!" : "Copy Referral Link"}
          </Button>

          {referralInfo?.totalInvites > 0 && (
            <Button
              onClick={() => setInvitationsModalVisible(true)}
              style={{
                background: "transparent",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
                height: "34px",
                fontSize: "12px",
                color: "#8c8c8c",
                fontWeight: "500",
              }}
              icon={<TeamOutlined />}
            >
              Manage ({referralInfo.totalInvites})
            </Button>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      <Modal
        title="Invite a Friend"
        visible={inviteModalVisible}
        onCancel={resetInviteForm}
        footer={null}
        closable={!inviting}
        maskClosable={!inviting}
      >
        <Form form={form} onFinish={handleInviteUser} layout="vertical">
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              {
                required: true,
                message: "Please enter your friend's email",
              },
              {
                type: "email",
                message: "Please enter a valid email",
              },
            ]}
            help={
              inviteError
                ? null
                : "Enter your friend's email address to send them an invitation"
            }
            validateStatus={inviteError ? "error" : ""}
          >
            <Input
              placeholder="Enter your friend's email"
              prefix={
                <UserAddOutlined
                  style={{
                    color: inviteError
                      ? inviteError.includes("couldn't be delivered")
                        ? "#faad14"
                        : "#ff4d4f"
                      : "#8B5CF6",
                  }}
                />
              }
              disabled={inviting}
              status={
                inviteError
                  ? inviteError.includes("couldn't be delivered")
                    ? "warning"
                    : "error"
                  : ""
              }
              onChange={() => inviteError && setInviteError("")}
            />
          </Form.Item>
          {inviteError && (
            <div
              style={{
                color: inviteError.includes("couldn't be delivered")
                  ? "#faad14"
                  : "#ff4d4f",
                marginBottom: "16px",
                fontSize: "12px",
                padding: "8px",
                background: inviteError.includes("couldn't be delivered")
                  ? "#fffbe6"
                  : "#fff2f0",
                border: `1px solid ${
                  inviteError.includes("couldn't be delivered")
                    ? "#ffe58f"
                    : "#ffccc7"
                }`,
                borderRadius: "4px",
              }}
            >
              <Text
                type={
                  inviteError.includes("couldn't be delivered")
                    ? "warning"
                    : "danger"
                }
                style={{ fontSize: "12px" }}
              >
                {inviteError.includes("couldn't be delivered") ? (
                  <ClockCircleOutlined style={{ marginRight: "6px" }} />
                ) : (
                  <InfoCircleOutlined style={{ marginRight: "6px" }} />
                )}
                {inviteError}
                {inviteError.includes("couldn't be delivered") && (
                  <div style={{ marginTop: "8px" }}>
                    <Button
                      size="small"
                      type="primary"
                      ghost
                      style={{ fontSize: "11px", marginTop: "4px" }}
                      onClick={() => {
                        // Attempt to resend the invitation
                        form.submit();
                      }}
                    >
                      Try Again
                    </Button>
                  </div>
                )}
              </Text>
            </div>
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              style={{
                width: "100%",
                background: "#8B5CF6",
                borderColor: "#8B5CF6",
              }}
              icon={<GiftOutlined />}
              loading={inviting}
              disabled={inviting}
            >
              {inviting ? "Sending..." : "Send Invitation"}
            </Button>
          </Form.Item>
        </Form>
        <div style={{ textAlign: "center", marginTop: "10px" }}>
          <Text type="secondary">
            Your friend will get an email with your referral link. You'll both
            receive 1000 credits when they sign up!
          </Text>
        </div>
      </Modal>

      {/* Invitations Management Modal */}
      <Modal
        title="Manage Invitations"
        visible={invitationsModalVisible}
        onCancel={() => setInvitationsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Tabs defaultActiveKey="all">
          <Tabs.TabPane tab="All Invitations" key="all">
            <List
              itemLayout="horizontal"
              dataSource={referralInfo?.invitations || []}
              locale={{ emptyText: "No invitations yet" }}
              renderItem={(invitation) => {
                const isPending = invitation.status === "pending";
                const isRewarded = invitation.status === "rewarded";
                const isRegistered = invitation.status === "registered";

                return (
                  <List.Item
                    actions={[
                      isPending && (
                        <Tooltip title="Retry sending invitation">
                          <Button
                            type="link"
                            icon={<SendOutlined />}
                            size="small"
                            onClick={() =>
                              handleRetryInvitation(invitation.email)
                            }
                          />
                        </Tooltip>
                      ),
                      isPending && (
                        <Tooltip title="Remove invitation">
                          <Button
                            type="link"
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={() =>
                              handleRemoveInvitation(invitation.email)
                            }
                          />
                        </Tooltip>
                      ),
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          style={{
                            backgroundColor: isRewarded
                              ? "#36b37e"
                              : isPending
                              ? "#faad14"
                              : "#8B5CF6",
                            color: "white",
                          }}
                        >
                          {invitation.email.charAt(0).toUpperCase()}
                        </Avatar>
                      }
                      title={invitation.email}
                      description={
                        <>
                          <div>
                            Status:{" "}
                            {isRewarded ? (
                              <Tag color="success">
                                <CheckCircleOutlined /> Rewarded
                              </Tag>
                            ) : isRegistered ? (
                              <Tag color="processing">
                                <CheckCircleOutlined /> Registered
                              </Tag>
                            ) : (
                              <Tag color="warning">
                                <ClockCircleOutlined /> Pending
                              </Tag>
                            )}
                          </div>
                          <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            {isPending &&
                              `Invited: ${moment(invitation.invitedAt).format(
                                "MMM D, YYYY"
                              )}`}
                            {isRegistered &&
                              !isRewarded &&
                              `Registered: ${moment(
                                invitation.registeredAt
                              ).format("MMM D, YYYY")}`}
                            {isRewarded &&
                              `Rewarded: ${moment(invitation.rewardedAt).format(
                                "MMM D, YYYY"
                              )}`}
                          </div>
                        </>
                      }
                    />
                  </List.Item>
                );
              }}
              pagination={{
                pageSize: 5,
                size: "small",
                hideOnSinglePage: true,
              }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Pending" key="pending">
            <List
              itemLayout="horizontal"
              dataSource={(referralInfo?.invitations || []).filter(
                (inv) => inv.status === "pending"
              )}
              locale={{ emptyText: "No pending invitations" }}
              renderItem={(invitation) => (
                <List.Item
                  actions={[
                    <Tooltip title="Retry sending invitation">
                      <Button
                        type="link"
                        icon={<SendOutlined />}
                        size="small"
                        onClick={() => handleRetryInvitation(invitation.email)}
                      />
                    </Tooltip>,
                    <Tooltip title="Remove invitation">
                      <Button
                        type="link"
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                        onClick={() => handleRemoveInvitation(invitation.email)}
                      />
                    </Tooltip>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{ backgroundColor: "#faad14", color: "white" }}
                      >
                        {invitation.email.charAt(0).toUpperCase()}
                      </Avatar>
                    }
                    title={invitation.email}
                    description={
                      <>
                        <div>
                          <Tag color="warning">
                            <ClockCircleOutlined /> Pending
                          </Tag>
                        </div>
                        <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                          Invited:{" "}
                          {moment(invitation.invitedAt).format("MMM D, YYYY")}
                        </div>
                      </>
                    }
                  />
                </List.Item>
              )}
              pagination={{
                pageSize: 5,
                size: "small",
                hideOnSinglePage: true,
              }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Successful" key="success">
            <List
              itemLayout="horizontal"
              dataSource={(referralInfo?.invitations || []).filter(
                (inv) =>
                  inv.status === "rewarded" || inv.status === "registered"
              )}
              locale={{ emptyText: "No successful invitations yet" }}
              renderItem={(invitation) => {
                const isRewarded = invitation.status === "rewarded";

                return (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          style={{
                            backgroundColor: isRewarded ? "#36b37e" : "#8B5CF6",
                            color: "white",
                          }}
                        >
                          {invitation.email.charAt(0).toUpperCase()}
                        </Avatar>
                      }
                      title={invitation.email}
                      description={
                        <>
                          <div>
                            {isRewarded ? (
                              <Tag color="success">
                                <CheckCircleOutlined /> Rewarded (+1000 credits)
                              </Tag>
                            ) : (
                              <Tag color="processing">
                                <CheckCircleOutlined /> Registered
                              </Tag>
                            )}
                          </div>
                          <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            {isRewarded
                              ? `Rewarded: ${moment(
                                  invitation.rewardedAt
                                ).format("MMM D, YYYY")}`
                              : `Registered: ${moment(
                                  invitation.registeredAt
                                ).format("MMM D, YYYY")}`}
                          </div>
                        </>
                      }
                    />
                  </List.Item>
                );
              }}
              pagination={{
                pageSize: 5,
                size: "small",
                hideOnSinglePage: true,
              }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Modal>
    </Card>
  );
};

export default ReferralCard;
